// A file under her answer in the brain's chat — T154f (28 Sep 2026),
// docs/design/one-assistant-2026-09-28.md § T154f.
//
// Natanel: "both the whatsapp and the website agent should have the same capabilites". On WhatsApp
// `send_file` and `send_profile` send the staff member the file itself (T148, T149); in the brain's
// chat the same two put it under the answer, and this is what draws it: a person's profile PDF,
// made when it is fetched (T151's route, the Profile PDF button's own), a file somebody sent
// kadmon, from its signed download (T107a, the thread's own), a client's PDF of profiles (T152's,
// its page's Download PDF), since T158 a Search's ranked top people (its own logged-in route), and
// since T512 a Search's job description (the Search page's button's).
// Nothing new is served: all five are the brain's logged-in routes, pressed from here.
import { useState } from 'react';
import type { ChatDownload } from '@vera/bff';
import { candidatesApi } from '../api/candidates.ts';
import { clientDocumentsApi } from '../api/clientDocuments.ts';
import { shortlistsApi } from '../api/shortlists.ts';
import { jobDescriptionsApi } from '../api/jobDescriptions.ts';
import { useMessageFile } from '../api/conversations.ts';
import { useRequest } from '../api/provider.tsx';
import { saveInBrowser } from '../profilePdf.ts';
import { Button } from '../ui/atoms.tsx';
import { WriteError } from '../ui/forms.tsx';

export function ChatDownloads({ files }: { files: ChatDownload[] }) {
  return (
    <div className="answer__downloads">
      {files.map((file) => (
        <DownloadButton key={downloadKey(file)} file={file} />
      ))}
    </div>
  );
}

function downloadKey(file: ChatDownload): string {
  if (file.kind === 'profile') {
    return `p:${(file.candidateIds ?? [file.candidateId]).join(',')}:${file.searchId ?? ''}:${file.roleId ?? ''}`;
  }
  if (file.kind === 'client_document') return `d:${file.documentId}`;
  if (file.kind === 'shortlist') return `s:${file.shortlistId}`;
  if (file.kind === 'job_description') return `j:${file.jobDescriptionId}`;
  return `m:${file.messageId}`;
}

function DownloadButton({ file }: { file: ChatDownload }) {
  const request = useRequest();
  const messageFile = useMessageFile();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const press = () => {
    setError(null);
    if (file.kind !== 'message_file') {
      setBusy(true);
      const bytes =
        file.kind === 'profile'
          ? // T277: several people as one PDF, read against the Search her answer named.
            candidatesApi(request).profilePdf(file.candidateId, {
              ...(file.roleId ? { roleId: file.roleId } : { searchId: file.searchId ?? null }),
              ...(file.candidateIds ? { ids: file.candidateIds } : {}),
            })
          : file.kind === 'shortlist'
            ? shortlistsApi(request).pdf(file.shortlistId)
            : file.kind === 'job_description'
              ? jobDescriptionsApi(request).pdf(file.jobDescriptionId)
              : clientDocumentsApi(request).pdf(file.documentId);
      void bytes
        .then((pdf) => saveInBrowser(pdf, file.name, 'application/pdf'))
        .catch(setError)
        .finally(() => setBusy(false));
      return;
    }
    // The response forces a download, so the brain stays where it is (ConversationScreen's own).
    messageFile.mutate(
      { conversationId: file.conversationId, messageId: file.messageId },
      { onSuccess: ({ url }) => window.location.assign(url), onError: setError },
    );
  };

  const pending = busy || messageFile.isPending;
  return (
    <span className="answer__download">
      <Button size="sm" icon="doc" onClick={press} disabled={pending}>
        {pending ? 'Preparing…' : file.name}
      </Button>
      <WriteError error={error} what="download it" />
    </span>
  );
}
